import { api, agencyApi } from '../lib/axios';
import { AgencyBlogResponseDto, PaginatedAgencyBlogResponse } from './admin/agencyBlog.admin.service';

export interface PaymentMethodField {
  fieldName: string;
  value: string;
}

export interface PaymentMethodData {
  paymentMethodId: string;
  fieldValues: PaymentMethodField[];
}

export interface AgencySignUpPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  /** Optional: payout methods are collected at the first payout request. */
  paymentMethods?: PaymentMethodData[];
}

export interface AgencySignInPayload {
  email: string;
  password: string;
}

export interface AgencyProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  code?: string;
  profilePicture?: string;
  emailVerified: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  paymentMethods: Array<{
    id: string;
    paymentMethodId: string;
    fieldData: Record<string, string>;
    paymentMethod: {
      id: string;
      name: string;
      description?: string;
      isActive: boolean;
      fields: Array<{
        id: string;
        fieldName: string;
        label: string;
        isRequired: boolean;
        placeHolder?: string;
      }>;
    };
  }>;
}

export interface AgencyAuthResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  code?: string;
  profilePicture?: string;
  emailVerified: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  access_token: string;
}

export interface UpdateAgencyProfilePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
}

export interface SuggestCodeResponse {
  code: string;
}

export interface ApplyCodePayload {
  code: string;
}

export interface AuthUrlDto {
  utm_source: string;
  url: string;
}

export interface AuthUrlsResponseDto {
  referrer_code: string;
  urls: AuthUrlDto[];
  main_url: string;
}

export interface ReferredBrand {
  id: string;
  companyName: string;
  niches: string[];
  commission: number;
  subscriptionType: string;
  status: 'Active' | 'Pending';
  joinedDate: string;
}

export interface ReferredBrandsTableResponse {
  brands: ReferredBrand[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ReferredBrandsSummaryResponse {
  totalBrands: number;
  activeBrands: number;
  totalCommission: number;
}

export interface PerformanceMetric {
  value: number;
  percentageChange: number;
  previousValue: number;
}

export interface ActivityItem {
  id: string;
  type: 'click' | 'conversion';
  source: string;
  date: string;
  description: string;
}

export interface AgencyAnalyticsResponse {
  performanceMetrics: {
    totalClicks: PerformanceMetric;
    conversions: PerformanceMetric;
    conversionRate: PerformanceMetric;
  };
  recentActivity: ActivityItem[];
}

export interface AgencyPayoutStatus {
  availableAmount: number;
  pendingAmount: number;
  paidAmount: number;
}

export interface AgencyPayoutItem {
  id: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
  paidAt?: string | null;
  referenceNumber?: string | null;
  paymentDetails?: {
    paymentMethodId?: string;
    paymentMethodName?: string;
    fieldData?: Record<string, string>;
  } | null;
}

export interface AgencyPayoutHistoryResponse {
  items: AgencyPayoutItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AgencyBrandRequest {
  id: string;
  brandDetailId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  message?: string;
  responseMessage?: string;
  createdAt: string;
  updatedAt: string;
  agency: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    code: string;
    profilePicture?: string;
  };
  brandDetail: {
    id: string;
    companyName: string;
    website?: string;
    niches: Array<{ name: string }>;
  };
}

export interface AgencyBrandRequestsResponse {
  requests: AgencyBrandRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AgencyBrandRequestStatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

export interface AvailableBrand {
  id: string;
  companyName: string;
  website?: string;
  description?: string;
  niches: Array<{ name: string }>;
}

export interface AvailableBrandsResponse {
  brands: AvailableBrand[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateBrandRequestPayload {
  brandDetailId: string;
  message?: string;
}

export interface RespondToBrandRequestPayload {
  status: 'APPROVED' | 'REJECTED';
  responseMessage?: string;
}

export const agencyService = {
  async signUp(data: AgencySignUpPayload): Promise<AgencyAuthResponse> {
    const response = await api.post<AgencyAuthResponse>('/agency/signup', data);
    return response.data;
  },

  async signIn(data: AgencySignInPayload): Promise<AgencyAuthResponse> {
    const response = await api.post<AgencyAuthResponse>('/agency/signin', data);
    return response.data;
  },

  async getProfile(): Promise<AgencyProfile> {
    const response = await agencyApi.get<AgencyProfile>('/agency/profile');
    return response.data;
  },

  async updateProfile(data: UpdateAgencyProfilePayload): Promise<AgencyProfile> {
    const response = await agencyApi.patch<AgencyProfile>('/agency/profile', data);
    return response.data;
  },

  async suggestCode(): Promise<SuggestCodeResponse> {
    const response = await agencyApi.get<SuggestCodeResponse>('/agency/suggest-code');
    return response.data;
  },

  async applyCode(data: ApplyCodePayload): Promise<AgencyProfile> {
    const response = await agencyApi.patch<AgencyProfile>('/agency/apply-code', data);
    return response.data;
  },

  async updatePaymentMethods(paymentMethods: PaymentMethodData[]): Promise<AgencyProfile> {
    const response = await agencyApi.patch<AgencyProfile>('/agency/payment-methods', { paymentMethods });
    return response.data;
  },

  async getBrandInstallationUrls(): Promise<AuthUrlsResponseDto> {
    const response = await agencyApi.get<AuthUrlsResponseDto>('/agency/auth-urls/');
    return response.data;
  },

  async getReferredBrandsTable(page: number = 1, limit: number = 10, search?: string): Promise<ReferredBrandsTableResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) {
      params.append('search', search);
    }
    
    const response = await agencyApi.get<ReferredBrandsTableResponse>(`/agency/brands/table?${params.toString()}`);
    return response.data;
  },

  async getReferredBrandsSummary(): Promise<ReferredBrandsSummaryResponse> {
    const response = await agencyApi.get<ReferredBrandsSummaryResponse>('/agency/brands/summary');
    return response.data;
  },

   async getAgencyBlogs(
      page: number = 1,
      limit: number = 10
    ): Promise<PaginatedAgencyBlogResponse> {
      try {
        const res = await agencyApi.get(
          `/agency-blog?page=${page}&limit=${limit}`
        );
        return res.data;
      } catch (error: any) {
        console.error("Error fetching agency blogs:", error);
        return {
          data: [],
          pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },

    async getAgencyBlogById(id: string): Promise<AgencyBlogResponseDto> {
        const res = await agencyApi.get(`/agency-blog/${id}`);
        return res.data.data || res.data.item || res.data;
      },
  

  async getAnalytics(): Promise<AgencyAnalyticsResponse> {
    const response = await agencyApi.get<AgencyAnalyticsResponse>('/agency/analytics');
    return response.data;
  },

  async getPayoutStatus(): Promise<AgencyPayoutStatus> {
    const response = await agencyApi.get<AgencyPayoutStatus>('/agency/payout/status');
    return response.data;
  },

  async requestAgencyCommission(): Promise<{ agencyRequestedAmount: number; payoutId: string }> {
    const response = await agencyApi.post<{ agencyRequestedAmount: number; payoutId: string }>('/agency/payout/request');
    return response.data;
  },

  async getPayoutHistory(page: number = 1, limit: number = 10): Promise<AgencyPayoutHistoryResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    const response = await agencyApi.get<AgencyPayoutHistoryResponse>(`/agency/payout/history?${params.toString()}`);
    return response.data;
  },

  async createBrandRequest(data: CreateBrandRequestPayload): Promise<AgencyBrandRequest> {
    const response = await agencyApi.post<AgencyBrandRequest>('/agency/brand-requests', data);
    return response.data;
  },

  async getAgencyBrandRequests(
    page: number = 1,
    limit: number = 20,
    status?: string,
  ): Promise<AgencyBrandRequestsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) {
      params.append('status', status);
    }
    
    const response = await agencyApi.get<AgencyBrandRequestsResponse>(`/agency/brand-requests?${params.toString()}`);
    return response.data;
  },

  async getAgencyBrandRequestStatusCounts(): Promise<AgencyBrandRequestStatusCounts> {
    const response = await agencyApi.get<AgencyBrandRequestStatusCounts>('/agency/brand-requests/status-counts');
    return response.data;
  },

  async getAvailableBrandsForRequest(
    page: number = 1,
    limit: number = 20,
    search?: string,
  ): Promise<AvailableBrandsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) {
      params.append('search', search);
    }
    
    const response = await agencyApi.get<AvailableBrandsResponse>(`/agency/available-brands?${params.toString()}`);
    return response.data;
  },

  async getBrandPendingRequests(): Promise<AgencyBrandRequest[]> {
    const response = await agencyApi.get<AgencyBrandRequest[]>('/agency/brand-requests/pending');
    return response.data;
  },

  async respondToBrandRequest(requestId: string, data: RespondToBrandRequestPayload): Promise<AgencyBrandRequest> {
    const response = await agencyApi.post<AgencyBrandRequest>(`/agency/brand-requests/${requestId}/respond`, data);
    return response.data;
  },
};